import { useEffect, useState, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { keys, last } from "lodash";

import {
  clearOptionError,
  selectOption,
  setOptionOtherText,
  selectSubOption,
  setSubOptionOtherText,
  setOptionError,
  setOption,
  setConsent,
  selectLanguage,
} from "state/modules/response";
import {
  isEmailValid,
  isStringNumeric,
  isCellPhonenValid,
  stub,
  usePrevious,
} from "utils";
import Countries from "assets/countries";
import {
  QuestionKind,
  ScoreLabel,
  ScreenType,
  ANIMATION_DURATION,
} from "consts";
import { setCountryCode } from "state/modules/customer";
import {
  getCustomerName,
  getCellphone,
} from "state/modules/customer/selectors";
import { fillPlaceholders, pickTheme } from "state/modules/survey/selectors";
import { navForward, navBackward } from "state/modules/navigation";
import {
  addOptionFile,
  removeOptionFile,
  addSubOptionFile,
  removeSubOptionFile,
  validateCurrentSection,
} from "state/modules/response";
import { ScrollContext } from "components";
import { setDivision } from "state/modules/division";
import * as selectors from "./selectors";
import { setCellphone } from "state/modules/customer/actions";

export const useNavigation = questionnaire => {
  const _section = useSelector(selectors.getSection);
  const isValid = useSelector(selectors.getIsValid);
  const sequence = useSelector(selectors.getSequence);
  const progress = useSelector(selectors.getSurveyCompletionPercentage);

  const prevSequence = usePrevious(sequence) || 1;
  const didSectionChange = prevSequence !== sequence;
  const [loading, section] = useDelayedState(didSectionChange, _section);

  const isLastScreen = isLastSection(section, questionnaire);
  const isFinished = isLastScreen ? "Finish" : "Next";

  const dispatch = useDispatch();

  const RTL = useSelector(selectors.getIsRTL);
  const questions = translateQuestions(section.questions, RTL);
  const [nextButton, previousButton] = translateNavButtons(isFinished, RTL);

  return {
    RTL,
    section,
    questions,
    isValid,
    sequence,
    progress,
    nextButton,
    previousButton: shouldShowPreviousButton(section, questionnaire)
      ? previousButton
      : null,
    navForward: loading ? stub : () => dispatch(navForward()),
    navBackward: loading ? stub : () => dispatch(navBackward()),
    totalSectionQuestions: section.questions.filter(
      q => q.app_screen_type !== ScreenType.CONSENT, // consent question does not occupy same space as other questions
    ).length,
  };
};

export const useSurveyTheme = () => {
  const logo = useSelector(selectors.getSurveyLogo);
  const questionnaire = useSelector(selectors.getSurveyQuestionnaire);

  const organization = useSelector(state => state.organization);
  const theme = pickTheme(questionnaire, organization);

  const background = useSelector(state =>
    selectors.getSurveyBackground(null, state),
  );

  return { logo, background, theme };
};

export const useFillPlaceholder = text => {
  const _state = useSelector(s => s);
  const sequence = useSelector(selectors.getSequence);
  const prevSequence = usePrevious(sequence) || 1;
  const didSectionChange = prevSequence !== sequence;
  const [, state] = useDelayedState(didSectionChange, _state);
  return fillPlaceholders(text, state);
};

export const useResponse = question => {
  const selectedOptions = useSelectedOptions(question);
  const dispatch = useDispatch();
  const isNavigating = useIsNavigating();
  const RTL = useSelector(selectors.getIsRTL);
  const options = translate(question.options, RTL);

  return {
    RTL,
    options,
    selectedOptions,
    singleSelect: question.max_option_selection_count === 1,
    selectOption: isNavigating
      ? stub // Prevent double navigation
      : question.is_master_question
      ? (subQuestion, option) => dispatch(selectOption(subQuestion, option))
      : option => dispatch(selectOption(question, option)),
  };
};

export const useOtherTextResponse = (question, option, subOption) => {
  const selectedOptions = useSelectedOptions(question);

  const _option = option || question.options[0];
  const selectedOption = selectedOptions[_option.virtual_id] || {
    other_text: "",
    subOptions: {},
  };

  const dispatch = useDispatch();
  const RTL = useSelector(selectors.getIsRTL);
  const customerName = useSelector(getCustomerName);
  let value = selectedOption.other_text;

  if (question.kind === QuestionKind.NAME) {
    if (!value && customerName) {
      selectedOptions[_option.virtual_id] = {
        other_text: customerName,
        subOptions: {},
      };
      value = customerName;
      dispatch(setOptionOtherText(question, _option, customerName));
    }
  }

  if (!subOption) {
    return {
      RTL,
      value: value,
      placeholder: getPlaceholder(question, _option, RTL),
      onChange: text => dispatch(setOptionOtherText(question, _option, text)),
    };
  }

  const selectedSubOption = selectedOption.subOptions[subOption.virtual_id] || {
    other_text: "",
  };

  return {
    RTL,
    value: selectedSubOption.other_text,
    placeholder: getPlaceholder(question, subOption, RTL),
    onChange: text =>
      dispatch(setSubOptionOtherText(question, _option, subOption, text)),
  };
};

export const useEmailAddressResponse = question => {
  const { onChange, ...rest } = useOtherTextResponse(question);
  const [option] = question.options;
  const hasError = useSelector(state =>
    selectors.getOptionError(state, option),
  );

  const dispatch = useDispatch();
  return {
    ...rest,
    hasError,
    onChange: text => {
      onChange(text);
      if (hasError && isEmailValid(text)) {
        dispatch(clearOptionError(option.id));
      }
    },
    validateEmail: text => {
      const isValid = isEmailValid(text);
      if (isValid && hasError) {
        dispatch(clearOptionError(option.id));
      } else if (!isValid && !hasError) {
        dispatch(setOptionError(option.id));
      }
    },
  };
};

export const useContactNumberResponse = question => {
  const { onChange, value, ...rest } = useOtherTextResponse(question);
  const [option] = question.options;
  const hasError = useSelector(state =>
    selectors.getOptionError(state, option),
  );
  const countryCode = useSelector(state => state.customer.country_code);
  const number = useSelector(getCellphone);

  const dispatch = useDispatch();
  return {
    ...rest,
    hasError,
    countryCode,
    value: number,
    onChange: text => {
      if (isStringNumeric(text) && text[0] != "0") {
        onChange("" + Countries[countryCode].dialCode + text);
        dispatch(setCellphone(text));
        if (hasError && isCellPhonenValid(text)) {
          dispatch(clearOptionError(option.id));
        }
      }
    },
    validateCellphone: text => {
      const isValid = isCellPhonenValid(text);
      if (isValid && hasError) {
        dispatch(clearOptionError(option.id));
      } else if (!isValid && !hasError) {
        dispatch(setOptionError(option.id));
      }
    },
    updateDialCode: (code, text) => {
      dispatch(setCountryCode(code));
      onChange("" + Countries[code].dialCode + text);
    },
  };
};

export const useDrilledDownResponse = question => {
  const selectedOptions = useSelectedOptions(question);
  const RTL = useSelector(selectors.getIsRTL);
  const options = translate(question.options, RTL).map(o => ({
    ...o,
    sub_options: translate(o.sub_options, RTL),
  }));
  const dispatch = useDispatch();
  return {
    RTL,
    options,
    selectedOptions,
    singleSelect: question.max_sub_option_selection_count === 1,
    selectSubOption: (option, subOption) =>
      dispatch(selectSubOption(question, option, subOption)),
  };
};

export const useListQuestion = question => {
  const [isPickerVisible, setPickerVisible] = useState(false);

  const RTL = useSelector(selectors.getIsRTL);
  const options = translate(question.options, RTL);
  const placeholder = getPlaceholder(question, question.options[0], RTL);

  const selectedQuestionOptions = useSelector(state =>
    selectors.getSelectedQuestionOptions(state, question),
  );
  const [selectedOption] = translate(selectedQuestionOptions, RTL);

  const dispatch = useDispatch();
  return {
    options,
    isPickerVisible,
    selectedOption,
    placeholder,
    RTL,
    setPickerVisible,
    onPress: selectedOpt => {
      dispatch(setOption(question, selectedOpt));
      setPickerVisible(false);
    },
  };
};

export const useStarQuestion = question => {
  const { selectedOptions, ...rest } = useResponse(question);

  const [selectedOptionId] = keys(selectedOptions).map(id => parseInt(id, 10));
  const selectedIndex = question.options.findIndex(
    o => o.virtual_id === selectedOptionId,
  );

  return {
    ...rest,
    selectedIndex,
  };
};

export const useLanguageQuestion = question => {
  const dispatch = useDispatch();

  return {
    ...useResponse(question),
    selectLanguage: option => dispatch(selectLanguage(question, option)),
  };
};

export const useConsentQuestion = question => {
  const [selectedOption] = useSelector(state =>
    selectors.getSelectedQuestionOptions(state, question),
  );

  const RTL = useSelector(selectors.getIsRTL);
  const dispatch = useDispatch();
  return {
    RTL,
    isSelected: selectedOption?.score === ScoreLabel.POSITIVE,
    onPress: () => dispatch(setConsent(question, selectedOption)),
  };
};

export const useMediaProps = (question, option, subOption) => {
  const dispatch = useDispatch();
  const option_ = option || question.options[0];

  const { mediaFiles, audioFile } = useSelector(state =>
    selectors.selectUploadedMedia(state, question, option_, subOption),
  );

  const addFile = subOption ? addSubOptionFile : addOptionFile;
  const removeFile = subOption ? removeSubOptionFile : removeOptionFile;

  const args = [question, option_];
  if (subOption) {
    args.push(subOption);
  }

  const scrollDownBy = useContext(ScrollContext);
  const [audioEnabled, setAudio] = useState(false);

  return {
    audioFile,
    mediaFiles,
    audioEnabled,
    audio: (subOption || option_).allow_audio,
    photo: (subOption || option_).allow_photo,
    video: (subOption || option_).allow_video,
    hasMedia: !!mediaFiles.length || !!audioFile.length || audioEnabled,
    enableAudio: () => {
      setAudio(!audioEnabled);
      scrollDownBy(300);
    },
    onCapture: (file, type) => {
      dispatch(addFile(...args, file, type));
    },
    removeFile: id => {
      dispatch(removeFile(...args, id));
    },
  };
};

export const useDivisionQuestion = () => {
  const [isPickerVisible, setPickerVisible] = useState(false);
  const dispatch = useDispatch();
  const divisions = useSelector(selectors.selectAllDivisions);
  const selectedDivision = useSelector(selectors.selectDivision);
  const divisionInfo = useSelector(selectors.selectDivisionInfo);
  return {
    divisions,
    isPickerVisible,
    setPickerVisible,
    selectedDivision,
    placeholder: divisionInfo.label,
    onPress: division => {
      dispatch(setDivision(division));
      dispatch(validateCurrentSection());
      setPickerVisible(false);
    },
  };
};

/**
 * When navigating back, during survey slider animation the selected options
 * get erased from state and options appear unselected. We don't want to update
 * selectedOptions in case of navigating back.
 */
const useSelectedOptions = question => {
  const [animating, setAnimating] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState({});

  const _selectedOptions = useSelector(state =>
    selectors.getSelectedOptions(state, question),
  );
  const sequence = useSelector(selectors.getSequence);
  const prevSequence = usePrevious(sequence) || 1;
  const didNavBack = sequence < prevSequence;

  useEffect(() => {
    if (didNavBack) {
      setAnimating(true);
    }
    if (!animating && !didNavBack) {
      setSelectedOptions(_selectedOptions ?? {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_selectedOptions, didNavBack]);

  return selectedOptions;
};

const useDelayedState = (delayOnChange, ...state) => {
  const [_state, _setState] = useState(state);
  const [delayed, setDelayed] = useState(false);
  useEffect(() => {
    if (!delayOnChange) {
      return;
    }

    setDelayed(true);
    setTimeout(() => {
      _setState(state);
      setDelayed(false);
    }, ANIMATION_DURATION / 2);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [delayOnChange]);
  return [delayed, ..._state];
};

const useIsNavigating = () => {
  const [isNavigating, setIsNavigating] = useState(false);
  const sequence = useSelector(selectors.getSequence);
  const prevSequence = usePrevious(sequence) || 1;
  const didSectionChange = prevSequence !== sequence;
  useEffect(() => {
    setIsNavigating(true);
    setTimeout(() => setIsNavigating(false), ANIMATION_DURATION / 2);
  }, [didSectionChange]);
  return isNavigating || didSectionChange;
};

export const OptionTextAsPlaceholderScreens = {
  [ScreenType.TEXT_AREA]: true,
  [ScreenType.TEXT_INPUT]: true,
  [ScreenType.EMAIL_ADDRESS]: true,
  [ScreenType.CONTACT_NUMBER]: true,
};

const langKey = isRTL => (isRTL ? "text_urdu" : "text");

const getPlaceholder = (question, option, RTL) => {
  if (RTL && question.kind === QuestionKind.AGE) {
    return "عمر";
  }
  if (RTL && !OptionTextAsPlaceholderScreens[question.app_screen_type]) {
    return "براہ کرم وضاحت کریں";
  }

  return OptionTextAsPlaceholderScreens[question.app_screen_type]
    ? option[langKey(RTL)]
    : option.other_text_placeholder;
};

const translate = (items, RTL) =>
  RTL ? items.map(i => ({ ...i, text: i.text_urdu || i.text })) : items;

const translateQuestions = (questions, RTL) => {
  const isLanguageQuestion = questions.some(
    q => q.kind === QuestionKind.LANGUAGE,
  );
  if (isLanguageQuestion) {
    return questions;
  }

  return translate(questions, RTL);
};

const translateNavButtons = (next, RTL) => {
  if (!RTL) {
    return [next, "Back"];
  }

  const translatedNext = next === "Next" ? "اگلا سوال" : "ختم";
  return [translatedNext, "پچھلا سوال"];
};

const isLastSection = (currentSection, questionnaire) => {
  if (currentSection.id !== last(questionnaire.questionnaire_sections).id) {
    return false;
  }

  const hasDrillDowns = currentSection.questions.some(question =>
    question.options.some(option => option.sub_options.length),
  );

  return !hasDrillDowns;
};

const isFirstSection = (currentSection, questionnaire) =>
  currentSection.id === questionnaire.questionnaire_sections[0].id &&
  currentSection.questions[0].app_screen_type !== ScreenType.DRILLED_DOWN;

const shouldShowPreviousButton = (currentSection, questionnaire) =>
  !(questionnaire.auto_start && isFirstSection(currentSection, questionnaire));
